import { QueryClient } from '@tanstack/react-query';

import { clearDynamicFilterOptionsCache } from '@/components/data-table/filters-v2';
import {
  ORGANIZATION_DEFAULTS_QUERY_KEY,
  ORGANIZATION_SUMMARY_QUERY_KEY,
} from '@/modules/common/hooks';
import {
  OVERHEADS_QUERY_KEY,
  OVERHEADS_SUMMARY_QUERY_KEY,
} from '@/modules/finance/overheads/constants/overheads-query-keys';
import { invalidateGuideQueries } from '@/modules/guide/utils/guide-query-invalidation';
import { ProjectDataReconciliationQueryKeys } from '@/modules/onboarding/projects-setup/types';
import { invalidateVisibleAssignments } from '@/modules/planner/helpers/data/invalidate-visible-assignments';
import { TIME_TRACKER_PROJECTS_QUERY_KEY_ROOT } from '@/modules/projects/hooks/use-time-tracker-projects';
import {
  REPORT_AVAILABLE_COLUMNS_QUERY_KEY_ROOT,
  REPORT_ELEMENT_DATA_PAGINATED_QUERY_KEY,
  REPORT_ELEMENT_DATA_QUERY_KEY,
} from '@/modules/reports/constants/reports-query-keys';
import { USER_PROFILE_QUERY_KEY } from '@/modules/settings/hooks/account/use-profile-avatar-config';
import { USER_GROUPS_QUERY_KEY } from '@/modules/settings/hooks/member-access/use-user-groups';
import {
  PAYMENT_DETAILS_QUERY_KEY,
  PAYMENT_DETAILS_SHORT_QUERY_KEY,
} from '@/modules/settings/hooks/payment-details/use-payment-details';
import { PERMISSIONS_QUERY_KEY } from '@/modules/settings/hooks/permissions/use-permissions';
import {
  SUGGESTED_APPROVERS_QUERY_KEY,
  TIME_OFF_QUERY_KEY_ROOT,
  WORKING_DAYS_QUERY_KEY,
} from '@/modules/time-off/constants/time-offs-query-keys';
import { invalidateTimeTrackingQueries } from '@/modules/time-tracking/utils/time-tracking-query-invalidation';

export { invalidateTimeTrackingQueries };

/**
 * Shared utility functions for invalidating related query caches across the application.
 * These functions ensure consistent cache invalidation patterns when entities are modified.
 */

/**
 * Invalidates all team-member-rates-on-project queries.
 * Use after any operation that can change rate configurations:
 * assignments (create/update), team member changes, project changes, organisation updates.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateTeamMemberRatesOnProjectQueries = (queryClient: QueryClient): void => {
  queryClient.removeQueries({ queryKey: ['team-member-rates-on-project'] });
};

/**
 * Invalidates the time-off suggested-approvers cache. Suggested approvers derive
 * from the requester's assignments / projects / team, so any change to those must
 * bust this (otherwise the create-request approvers prefill goes stale).
 * Called from the project + team-member invalidators and the assignment mutations.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateSuggestedApproversQueries = (queryClient: QueryClient): void => {
  queryClient.invalidateQueries({ queryKey: SUGGESTED_APPROVERS_QUERY_KEY });
};

/**
 * Invalidates the project-overheads list and summary. Overhead rows embed
 * project/client display data and their statuses are driven by invoice
 * generation/deletion, so call this after project, client, or invoice changes
 * (as well as after overhead mutations themselves).
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateOverheadsQueries = async (queryClient: QueryClient): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: [OVERHEADS_QUERY_KEY] }),
    queryClient.invalidateQueries({ queryKey: [OVERHEADS_SUMMARY_QUERY_KEY] }),
  ]);
};

/**
 * Invalidates invoice lists and detail queries. Invoice details are keyed beneath
 * the invoices root, so one root invalidation refreshes every invoice surface.
 */
export const invalidateInvoiceQueries = async (queryClient: QueryClient): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: ['invoices'] });
};

/** Invalidates bill lists and detail queries after a bill mutation. */
export const invalidateBillQueries = async (queryClient: QueryClient): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: ['bills'] });
};

/**
 * Invalidates the Time Tracking modal's project picker cache
 * (`/api/projects/time-tracker/search`). Its results embed both project details
 * and a per-member "booked" flag derived from assignments, so call this after any
 * project change (name, color, archive, etc.) AND after any assignment change
 * (create/update/delete/duplicate/cut) that could flip which projects count as booked.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateTimeTrackerProjectsQueries = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: [TIME_TRACKER_PROJECTS_QUERY_KEY_ROOT] });
};

/**
 * Invalidates all project-related queries.
 * Use after operations that affect projects (create, update, delete, archive).
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateProjectQueries = async (queryClient: QueryClient): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: ['projects'] });
  await queryClient.invalidateQueries({ queryKey: [ProjectDataReconciliationQueryKeys.Projects] });
  await queryClient.invalidateQueries({
    queryKey: [ProjectDataReconciliationQueryKeys.ValidationReport],
  });
  // Invalidate all individual project detail queries (matches ['project', projectId])
  // Use refetchType: 'active' to force refetch of currently active queries
  await queryClient.invalidateQueries({
    predicate: query => query.queryKey[0] === 'project',
    refetchType: 'active',
  });
  await queryClient.invalidateQueries({ queryKey: ['short-projects'] });
  await invalidateTimeTrackerProjectsQueries(queryClient);
  // Invalidate project changes queries (matches ['project-changes', projectId, filters])
  // Changes tab data may be affected by project updates (new snapshots created)
  await queryClient.invalidateQueries({ queryKey: ['project-changes'] });
  // Clear filters-v2 in-memory optionsCache for project filter dropdowns.
  // Time-tracking approvals use 'projectIds' as the filter name.
  clearDynamicFilterOptionsCache('projectIds');
  clearDynamicFilterOptionsCache('project');
  // Project changes can alter the time-off suggested approvers.
  invalidateSuggestedApproversQueries(queryClient);
  // Overhead rows embed project name/color/status.
  await invalidateOverheadsQueries(queryClient);
};

/**
 * Invalidates all team member-related queries.
 * Use after operations that affect team members (create, update, delete, archive).
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateTeamMemberQueries = async (queryClient: QueryClient): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: ['team-members'] });
  await queryClient.invalidateQueries({ queryKey: ['team-members-data-reconciliation'] });
  await queryClient.invalidateQueries({ queryKey: ['team-members-validation-report'] });

  // Remove team member dropdown caches completely to ensure fresh data on next load
  // This is necessary because dropdown queries are disabled when closed,
  // so invalidateQueries won't trigger a refetch
  // Use predicate to match all team-members-short variants (including ['team-members-short', 'managers'])
  queryClient.removeQueries({
    predicate: query => query.queryKey[0] === 'team-members-short',
  });
  queryClient.removeQueries({ queryKey: ['team-member-managers'] });
  // Clear all v1 filter dropdown caches in one sweep — any filter that shows team member-derived
  // data (user, country, city, manager, etc.) must refresh after a team member change.
  // Both prefixes are exclusively used by v1 filter components, so blanket removal is safe.
  queryClient.removeQueries({ queryKey: ['filter-entities'] });
  queryClient.removeQueries({ queryKey: ['filter-paginated'] });
  // Clear filters-v2 in-memory optionsCache for team member filter dropdowns.
  // Time-tracking approvals use 'memberIds' as the filter name.
  clearDynamicFilterOptionsCache('memberIds');
  // Invalidate team member changes queries (matches ['team-member-changes', teamMemberId, filters])
  // Changes tab data may be affected by team member updates (new snapshots created)
  await queryClient.invalidateQueries({ queryKey: ['team-member-changes'] });
  // Member access data in settings shows per-member roles/groups and reflects team member state
  // refetchType: 'all' ensures inactive queries (page not currently open) are also invalidated
  // so data is fresh when the user navigates to the members page
  await queryClient.invalidateQueries({ queryKey: ['member-access'], refetchType: 'active' });
  // Team member changes can alter the time-off suggested approvers.
  invalidateSuggestedApproversQueries(queryClient);
};

/**
 * Invalidates all client-related queries.
 * Use after operations that affect clients (create, update, delete, archive).
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateClientQueries = async (queryClient: QueryClient): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: ['clients'] });
  await queryClient.invalidateQueries({ queryKey: ['clients-data-reconciliation'] });
  await queryClient.invalidateQueries({ queryKey: ['clients-validation-report'] });

  // Remove clients-short cache completely to ensure fresh data on next load
  // This is necessary because the dropdown query is disabled when closed,
  // so invalidateQueries won't trigger a refetch
  queryClient.removeQueries({ queryKey: ['clients-short'] });
  // Overhead rows embed the client name.
  await invalidateOverheadsQueries(queryClient);
};

/**
 * Invalidates queries for entities commonly affected by team member changes.
 * Use after team member operations (create, update, delete, archive) to ensure
 * related data (projects, clients) stay in sync.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateTeamMemberRelatedQueries = async (
  queryClient: QueryClient
): Promise<void> => {
  await invalidateTeamMemberQueries(queryClient);
  // Team member changes may affect projects (assignments, managers)
  await invalidateProjectQueries(queryClient);
  // Team member changes (rates, role, seniority) affect rates-on-project results
  invalidateTeamMemberRatesOnProjectQueries(queryClient);
  // Team member state changes (capacity, rates, etc.) affect planner assignment data
  invalidateVisibleAssignments(queryClient);
  // Time tracking lists and approval screens display team member names/rates,
  // so they must refresh when team member data changes.
  await invalidateTimeTrackingQueries(queryClient);
};

/**
 * Invalidates all team member-related queries including a specific team member's detail query.
 * Use after operations on a specific team member (update, archive, unarchive, invite, etc.)
 * to ensure both the list and the individual member's data stay in sync.
 *
 * @param queryClient - TanStack Query client instance
 * @param memberId - The ID of the specific team member to invalidate
 *
 * @example
 * ```typescript
 * // In a mutation's onSuccess handler
 * onSuccess: async () => {
 *   await invalidateTeamMemberWithRelatedQueries(queryClient, teamMember.id);
 * }
 * ```
 */
export const invalidateTeamMemberWithRelatedQueries = async (
  queryClient: QueryClient,
  memberId: string | number
): Promise<void> => {
  await invalidateTeamMemberRelatedQueries(queryClient);
  await queryClient.invalidateQueries({ queryKey: ['team-member', memberId] });
};

/**
 * Invalidates queries for entities commonly affected by client changes.
 * Use after client operations (create, update, delete, archive) to ensure
 * related data (projects, team members) stay in sync.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateClientRelatedQueries = async (queryClient: QueryClient): Promise<void> => {
  await invalidateClientQueries(queryClient);
  // Client changes may affect team members (managers)
  await invalidateTeamMemberQueries(queryClient);
  // Client changes affect associated projects
  await invalidateProjectQueries(queryClient);
};

/**
 * Invalidates queries for entities commonly affected by project changes.
 * Use after project operations (create, update, delete, archive) to ensure
 * related data stays in sync. Note: This does NOT invalidate team member or client queries
 * as project changes typically don't affect those entities.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateProjectRelatedQueries = async (queryClient: QueryClient): Promise<void> => {
  await invalidateProjectQueries(queryClient);
  // Project changes (billing config, rate cards) affect rates-on-project results
  invalidateTeamMemberRatesOnProjectQueries(queryClient);
  // Time tracking lists and approval screens are grouped by project,
  // so they must refresh when project data changes (name, color, archive, etc.).
  await invalidateTimeTrackingQueries(queryClient);
};

/**
 * Invalidates all department-related queries.
 * Use after operations that affect departments (create, update, delete).
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateDepartmentQueries = async (queryClient: QueryClient): Promise<void> => {
  queryClient.removeQueries({ queryKey: ['departments'] });
  // Remove filter dropdown caches so the next open shows fresh department data.
  // filter-paginated: used by FilterDynamicAutocompletePaginated (team page filters, v1)
  // filter-entities: used by FilterDynamicAutocomplete with TanStack Query (planner filters, v1)
  // clearDynamicFilterOptionsCache: used by DynamicAutocompleteFilterSubmenu (filters-v2)
  queryClient.removeQueries({ queryKey: ['filter-paginated', 'department'] });
  queryClient.removeQueries({ queryKey: ['filter-entities', 'department'] });
  clearDynamicFilterOptionsCache('department');
  // Time-tracking approvals use 'departmentIds' as the filter name
  clearDynamicFilterOptionsCache('departmentIds');
};

/**
 * Invalidates all role-related queries.
 * Use after operations that affect roles (create, update, delete).
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateRoleQueries = async (queryClient: QueryClient): Promise<void> => {
  queryClient.removeQueries({ queryKey: ['roles'] });
  // Remove filter dropdown caches so the next open shows fresh role data.
  queryClient.removeQueries({ queryKey: ['filter-paginated', 'role'] });
  queryClient.removeQueries({ queryKey: ['filter-entities', 'role'] });
  clearDynamicFilterOptionsCache('role');
};

/**
 * Invalidates all seniority-related queries.
 * Use after operations that affect seniorities (create, update, delete).
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateSeniorityQueries = async (queryClient: QueryClient): Promise<void> => {
  queryClient.removeQueries({ queryKey: ['seniorities'] });
  // Remove filter dropdown caches so the next open shows fresh seniority data.
  queryClient.removeQueries({ queryKey: ['filter-paginated', 'seniority'] });
  queryClient.removeQueries({ queryKey: ['filter-entities', 'seniority'] });
  clearDynamicFilterOptionsCache('seniority');
};

/**
 * Invalidates queries for entities commonly affected by department changes.
 * Use after department operations (create, update, delete) to ensure
 * related data (team members) stay in sync.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateDepartmentRelatedQueries = async (
  queryClient: QueryClient
): Promise<void> => {
  await invalidateDepartmentQueries(queryClient);
  await invalidateTeamMemberRelatedQueries(queryClient);
  await invalidateGuideQueries(queryClient);
};

/**
 * Invalidates queries for entities commonly affected by role changes.
 * Use after role operations (create, update, delete) to ensure
 * related data (team members) stay in sync.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateRoleRelatedQueries = async (queryClient: QueryClient): Promise<void> => {
  await invalidateRoleQueries(queryClient);
  await invalidateTeamMemberRelatedQueries(queryClient);
  await invalidateGuideQueries(queryClient);
};

/**
 * Invalidates queries for entities commonly affected by seniority changes.
 * Use after seniority operations (create, update, delete) to ensure
 * related data (team members) stay in sync.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateSeniorityRelatedQueries = async (
  queryClient: QueryClient
): Promise<void> => {
  await invalidateSeniorityQueries(queryClient);
  await invalidateTeamMemberRelatedQueries(queryClient);
  await invalidateGuideQueries(queryClient);
};

/**
 * Invalidates queries for entities commonly affected by integration status.
 * Use after manual synchronization invocation to ensure the integration status
 * and any related data that depends on it stay in sync.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateIntegrationStatusRelatedQueries = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: ['integrationStatus'] });
};

/**
 * Invalidates organization defaults query.
 * Use after operations that affect organization defaults (currency, rate source, rate).
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateOrganizationDefaultsQueries = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: ORGANIZATION_DEFAULTS_QUERY_KEY });
};

/**
 * Invalidates organization summary query.
 * Use after operations that affect organization summary (name, logo, etc.).
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateOrganizationSummaryQueries = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: ORGANIZATION_SUMMARY_QUERY_KEY });
};

export const ORGANIZATION_QUERY_KEY = ['organization'];

/**
 * Invalidates all organization-related queries (full org data + summary).
 * Use after any operation that modifies the organization (name, logo, settings, etc.).
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateOrganizationQueries = async (queryClient: QueryClient): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEY });
  await queryClient.invalidateQueries({ queryKey: ORGANIZATION_SUMMARY_QUERY_KEY });
  // The org work schedule decides which days are working, so the (indefinitely
  // cached) time-off working-days results become stale when org settings change.
  await queryClient.invalidateQueries({ queryKey: WORKING_DAYS_QUERY_KEY });
};

/**
 * Invalidates user profile query.
 * Use after operations that affect the current user's profile (name, avatar).
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateUserProfileQueries = async (queryClient: QueryClient): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEY });
};

/**
 * Invalidates all time-off queries (balances + requests list) plus everything an
 * approved/declined/created time-off can change: planner assignments (a booked
 * time-off blocks capacity) and the time-tracking views + approvals (which render
 * per-day time-off state).
 *
 * Use after creating/canceling/updating/approving/declining a time-off request.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateTimeOffQueries = async (queryClient: QueryClient): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: [TIME_OFF_QUERY_KEY_ROOT] });
  clearDynamicFilterOptionsCache('typeIds');

  // A time-off change affects planner capacity and the time-tracking/approvals views.
  invalidateVisibleAssignments(queryClient);
  await invalidateTimeTrackingQueries(queryClient);
};

/**
 * Invalidates time-off settings queries and dependent employee time-off data.
 * Use after scheduling or deleting a time-off policy change.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateTimeOffSettingsQueries = async (queryClient: QueryClient): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: ['time-off-types'] });
  await queryClient.invalidateQueries({ queryKey: ['time-off-type-changes'] });
  await invalidateTimeOffQueries(queryClient);
};

/**
 * Marks team-page lists, dropdowns and dependent surfaces stale WITHOUT firing any
 * immediate network request — they refetch lazily when the user next navigates there.
 *
 * Shared by the members-page mutation helpers below: a group save / invite changes a
 * member's group/status, which the team page and member dropdowns display, but the user
 * is on the members page, so there is no reason to refetch those surfaces right now.
 *
 * @param queryClient - TanStack Query client instance
 */
const markTeamMemberSurfacesStale = async (queryClient: QueryClient): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: ['team-members'], refetchType: 'none' });
  await queryClient.invalidateQueries({ queryKey: ['team-member-changes'], refetchType: 'none' });
  // Dropdown / filter caches are disabled when closed, so removing them is the only way
  // to guarantee fresh data on next open (without an eager refetch now).
  queryClient.removeQueries({ predicate: query => query.queryKey[0] === 'team-members-short' });
  queryClient.removeQueries({ queryKey: ['team-member-managers'] });
  queryClient.removeQueries({ queryKey: ['filter-entities'] });
  queryClient.removeQueries({ queryKey: ['filter-paginated'] });
  clearDynamicFilterOptionsCache('memberIds');
};

/**
 * Refresh after a member-access mutation made FROM the members page itself — a group
 * save or a bulk invite. Both change a member's group/status, so the same refresh applies.
 *
 * `invalidateQueries` defaults to `refetchType: 'active'`: it refetches only the list
 * currently on screen (one request) and marks the other member-access caches stale so
 * they refetch lazily on revisit — NOT all at once (that storm was the bug we removed).
 * Serving a stale frame on revisit is safe because the edits store's `hydrate` is
 * server-authoritative for unedited rows: the background refetch's fresh data overwrites
 * any briefly-served stale values.
 *
 * Team-page / dropdown surfaces are marked stale lazily (no immediate request) since the
 * user is on the members page.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateAfterMemberAccessChange = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: ['member-access'] });
  await markTeamMemberSurfacesStale(queryClient);
};

/**
 * Invalidates custom attribute definition queries plus the team member data that
 * displays/filters by those attributes. Use after creating/updating/deleting a
 * custom attribute definition in settings.
 *
 * The `['custom-attributes', ...]` predicate covers both the settings-scoped and
 * the team-page (`['custom-attributes', 'USER']`) query keys.
 *
 * @param queryClient - TanStack Query client instance
 */
/**
 * Invalidates the payment-details list and the short selector list. Use after
 * creating/updating/deleting a payment-details entry on the Finance settings page
 * so the Generate Invoices selector options (and its derived org default) refresh.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidatePaymentDetailsQueries = async (queryClient: QueryClient): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: PAYMENT_DETAILS_QUERY_KEY });
  await queryClient.invalidateQueries({ queryKey: PAYMENT_DETAILS_SHORT_QUERY_KEY });
};

export const invalidateCustomAttributeQueries = async (queryClient: QueryClient): Promise<void> => {
  await queryClient.invalidateQueries({
    predicate: query => query.queryKey[0] === 'custom-attributes',
    refetchType: 'all',
  });
  // Team members carry custom-attribute values and the filter configs are derived
  // from the attribute definitions, so refresh team data too.
  await invalidateTeamMemberQueries(queryClient);
  // Reports offer member custom attributes as dimensions (`GET /reports/columns`), and saved
  // elements drop a deleted attribute from their grouping — refresh both.
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: REPORT_AVAILABLE_COLUMNS_QUERY_KEY_ROOT }),
    queryClient.invalidateQueries({ queryKey: REPORT_ELEMENT_DATA_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: REPORT_ELEMENT_DATA_PAGINATED_QUERY_KEY }),
  ]);
};

/**
 * Invalidates the permissions matrix. The guide surfaces derive from the granted policies,
 * so they are refreshed alongside it.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidatePermissionsQueries = async (queryClient: QueryClient): Promise<void> => {
  await queryClient.invalidateQueries({ queryKey: PERMISSIONS_QUERY_KEY });
  await invalidateGuideQueries(queryClient);
};

/**
 * Invalidates everything that renders the organization's user groups: the permissions matrix, the
 * `['user-groups']` selector cache (members page group column + team invite modal) and the
 * member-access rows, whose group assignment a delete rewrites via `replacementGroupId`.
 *
 * `removeQueries` rather than `invalidateQueries` for the selector cache: `useUserGroups`
 * sets a 10 minute staleTime and the invite-modal copy is `enabled`-gated, so an invalidate
 * would not refresh a dropdown that is currently closed.
 *
 * The matrix is included so every group mutation has ONE invalidation call and cannot refresh
 * half the screen. Awaiting it matters: the permissions query sets no staleTime, so the refetch
 * starts immediately and this promise resolves only once it settles — callers that close a modal
 * afterwards then close onto fresh data instead of flashing the pre-mutation matrix.
 *
 * @param queryClient - TanStack Query client instance
 */
export const invalidateUserGroupRelatedQueries = async (
  queryClient: QueryClient
): Promise<void> => {
  await invalidatePermissionsQueries(queryClient);
  queryClient.removeQueries({ queryKey: USER_GROUPS_QUERY_KEY });
  await invalidateAfterMemberAccessChange(queryClient);
};
